import { useAppSelector } from '@/hooks'
import { selectPendingRockSetup, selectTenant } from '@/features/auth/authSlice'
import { hasUnsettledItem, useSetupRun } from '@/hooks/useSetupRun'
import { runKeysOf } from './autoSetupPlan'
import { useActiveRunRecord } from './activeSetupRun'

const LIVE_STATUSES = ['pending', 'running']
const SETTLED_STATUSES = ['done', 'failed', 'blocked']

/**
 * The app shell's view of a background Rock setup run (25i), for the banner:
 * the run started on this page load (published by `useAutoSetupRun`), else
 * the one `/me.pending_rock_setup.setup_run` names. Polls the run while it
 * is active, so the count moves; `/me`'s own counts stand in until the first
 * poll answers.
 *
 * @returns {{isActive: boolean, done: number, total: number}}
 */
export function useActiveSetupProgress() {
  const tenantId = useAppSelector(selectTenant)?.id
  const pending = useAppSelector(selectPendingRockSetup)
  const published = useActiveRunRecord(tenantId)
  const meRun = pending?.setup_run && typeof pending.setup_run === 'object' ? pending.setup_run : null

  const connectionId = published?.connectionId || pending?.connection_id
  const runId = published?.runId || meRun?.id
  const fromMe = meRun && meRun.id === runId ? meRun : null
  const isKnownLive = Boolean(published) || LIVE_STATUSES.includes(fromMe?.status)
  const runQuery = useSetupRun(isKnownLive ? connectionId : undefined, isKnownLive ? runId : undefined)
  const data = runQuery.data

  if (!isKnownLive) return { isActive: false, done: 0, total: 0 }
  if (data) {
    const items = Array.isArray(data.items) ? data.items : []
    const plannedTotal = published ? runKeysOf(published.plan || []).length : Number(fromMe?.total) || 0
    return {
      isActive: hasUnsettledItem(data),
      done: items.filter((item) => SETTLED_STATUSES.includes(item.status)).length,
      total: Math.max(items.length, plannedTotal),
    }
  }
  return {
    isActive: true,
    // `/me` counts successes as `done` and blocked items inside `failed`: both are finished.
    done: (Number(fromMe?.done) || 0) + (Number(fromMe?.failed) || 0),
    total: Number(fromMe?.total) || (published ? runKeysOf(published.plan || []).length : 0),
  }
}

export default useActiveSetupProgress
